// Heruntergeladene Vertrags-HTML eigenständig machen (seit 12.10.2026).
//
// Anlass: Der Vermittlervertrag als HTML zeigte nur das Deckblatt. Die Vorlagen
// binden contract-pagination.js relativ ein. Als lokale Datei (oder als blob: im
// neuen Tab) findet der Browser das Skript nicht, #contract-pages bleibt leer, und
// #contract-source ist per CSS ausgeblendet. Hier wird das Skript deshalb
// eingebettet und das Logo als data:-URI gesetzt - genau wie
// buildStandaloneContractHtml in api/contracts/usage.js beim Bulk-Download.
//
// Beim erneuten Hochladen stört das eingebettete Skript nicht: vertragBearbeiten.js
// entfernt alle Skripte und hängt die Paginierung wieder als Datei an.
//
// Das Logo steht in einem Vertrag vier- bis fünfmal und kostet eingebettet je rund
// 200 KB. Ein hochgeladener Vertrag geht beim Abschließen als HTML an die
// PDF-Erzeugung, und die nimmt höchstens 2 MB (MAX_CONTRACT_HTML_BYTES in
// api/_lib/contractPdf.js). Würde das Einbetten die Datei über LOGO_HOECHSTENS_BYTES
// heben, zeigt das Logo deshalb absolut auf vertrag.en-power.eu – vom Rechner
// geöffnet fehlt es dann ohne Internet. Gemessen am 16.10.2026: Pachtvertrag
// Kleinspeicher mit Lageplan 652 KB, mit eingebetteten Logos 1,48 MB;
// Vermittlervertrag 68 KB, eingebettet 0,9 MB. Die Grenze von 1,6 MB lässt beiden
// ihr Logo (Nutzer: „das Logo ist nicht im Vertrag“) und mindestens 400 KB Luft
// bis zur PDF-Grenze.

const PAGINIERUNG_TAG = /<script\b[^>]*\bsrc=(['"])[^'"]*contract-pagination\.js(?:\?[^'"]*)?\1[^>]*>\s*<\/script>/i;
const LOGO_VERWEIS = /(["'])(?:[^"']*\/)?enpower-logo-transparent\.png(?:\?[^"']*)?\1/gi;

export const PAGINIERUNG_PFAD = '/contracts/contract-pagination.js';
export const LOGO_PFAD = '/contracts/enpower-logo-transparent.png';
export const LOGO_HOECHSTENS_BYTES = 1.6 * 1024 * 1024;
export const NUR_ONLINE_HINWEIS = 'Das Seitenskript ließ sich nicht einbetten – die Datei zeigt alle Seiten nur mit Internetverbindung.';

/** Größe in UTF-8-Bytes, wie sie hochgeladen würde. */
export function utf8Laenge(text) {
  const s = String(text || '');
  let bytes = 0;
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) { bytes += 4; i += 1; }
    else bytes += 3;
  }
  return bytes;
}

export function hatVerlinktePaginierung(html) {
  return PAGINIERUNG_TAG.test(String(html || ''));
}

/** Ersetzt das verlinkte Paginierungs-Skript durch seinen Code. */
export function bettePaginierungEin(html, code) {
  const quelle = String(html || '');
  const skript = String(code || '');
  // Ein </script> im Code würde das umschließende Tag vorzeitig beenden.
  if (!skript.trim() || /<\/script/i.test(skript) || !PAGINIERUNG_TAG.test(quelle)) return quelle;
  return quelle.replace(PAGINIERUNG_TAG, () => `<script>\n${skript}\n</script>`);
}

/**
 * Notlösung, wenn der Code nicht ladbar ist: absolute Adresse statt relativer.
 * Dann baut die Datei ihre Seiten wenigstens mit Internetverbindung auf.
 */
export function verlinkePaginierungAbsolut(html, origin) {
  const quelle = String(html || '');
  if (!origin) return quelle;
  return quelle.replace(PAGINIERUNG_TAG, (tag) => tag.replace(
    /\bsrc=(['"])[^'"]*?(contract-pagination\.js(?:\?[^'"]*)?)\1/i,
    (_treffer, zeichen, datei) => `src=${zeichen}${origin}/contracts/${datei}${zeichen}`,
  ));
}

/** Ersetzt jeden Verweis auf das Vertragslogo durch die übergebene data:-URI. */
export function betteLogoEin(html, datenUri) {
  const quelle = String(html || '');
  if (!/^data:image\//i.test(String(datenUri || ''))) return quelle;
  return quelle.replace(LOGO_VERWEIS, () => `"${datenUri}"`);
}

/** Jeder Verweis auf das Vertragslogo zeigt danach absolut auf den Server. */
export function verlinkeLogoAbsolut(html, origin) {
  const quelle = String(html || '');
  if (!origin) return quelle;
  return quelle.replace(LOGO_VERWEIS, () => `"${origin}${LOGO_PFAD}"`);
}

async function ladeText(pfad) {
  const antwort = await fetch(pfad, { cache: 'no-store' });
  if (!antwort.ok) throw new Error(`${pfad}: ${antwort.status}`);
  return antwort.text();
}

async function ladeDatenUri(pfad) {
  const antwort = await fetch(pfad, { cache: 'no-store' });
  if (!antwort.ok) throw new Error(`${pfad}: ${antwort.status}`);
  const blob = await antwort.blob();
  return new Promise((resolve, reject) => {
    const leser = new FileReader();
    leser.onload = () => resolve(String(leser.result || ''));
    leser.onerror = () => reject(leser.error || new Error('Logo nicht lesbar.'));
    leser.readAsDataURL(blob);
  });
}

/**
 * Bettet Paginierung und Logo ein. `vollstaendig` ist false, wenn die Paginierung
 * nicht eingebettet werden konnte - dann zeigt die Datei ihre Seiten nur online.
 * Das Logo wird eingebettet, solange die Datei danach höchstens
 * `logoHoechstensBytes` groß ist; sonst (oder wenn es nicht ladbar ist) zeigt es
 * absolut auf den Server. Das zählt nicht als unvollständig.
 */
export async function machVertragsHtmlEigenstaendig(html, {
  holeText = ladeText,
  holeDatenUri = ladeDatenUri,
  origin = (typeof window !== 'undefined' && window.location ? window.location.origin : ''),
  logoHoechstensBytes = LOGO_HOECHSTENS_BYTES,
} = {}) {
  let ergebnis = String(html || '');
  let vollstaendig = true;

  if (hatVerlinktePaginierung(ergebnis)) {
    let eingebettet = ergebnis;
    try {
      eingebettet = bettePaginierungEin(ergebnis, await holeText(PAGINIERUNG_PFAD));
    } catch (fehler) {
      console.error('Paginierung nicht eingebettet:', fehler);
    }
    if (hatVerlinktePaginierung(eingebettet)) {
      vollstaendig = false;
      ergebnis = verlinkePaginierungAbsolut(ergebnis, origin);
    } else {
      ergebnis = eingebettet;
    }
  }

  let logo = 'ohne';
  if (/enpower-logo-transparent\.png/i.test(ergebnis)) {
    let mitLogo = '';
    try {
      mitLogo = betteLogoEin(ergebnis, await holeDatenUri(LOGO_PFAD));
    } catch (fehler) {
      console.error('Logo nicht eingebettet:', fehler);
    }
    if (mitLogo && !/enpower-logo-transparent\.png/i.test(mitLogo) && utf8Laenge(mitLogo) <= logoHoechstensBytes) {
      ergebnis = mitLogo;
      logo = 'eingebettet';
    } else {
      ergebnis = verlinkeLogoAbsolut(ergebnis, origin);
      logo = 'verlinkt';
    }
  }

  return { html: ergebnis, vollstaendig, logo };
}
